import { createRoot } from "react-dom/client";
import AppErrorBoundary from "@/components/AppErrorBoundary";
import App from "./App.tsx";
import "./index.css";

// ── Guard against browser-translation DOM crashes ──────────────────────────
// Mobile Chrome's auto-translate (Google Translate) rewrites text nodes in
// place, leaving React's virtual DOM out of sync. React then calls
// insertBefore / removeChild with a reference node the translator already
// moved, throwing "NotFoundError: ... not a child of this node" — which on the
// multi-step /apply wizard tripped the error boundary and lost the user's
// place mid-form. Make these two ops defensive: if the reference is no longer a
// child of the parent, no-op (removeChild) or append (insertBefore) instead of
// throwing. Well-known mitigation for facebook/react#11538.
if (typeof Node === "function" && Node.prototype) {
  const originalRemoveChild = Node.prototype.removeChild;
  Node.prototype.removeChild = function (this: Node, child: Node) {
    if (child.parentNode !== this) {
      if (import.meta.env.DEV) console.warn("removeChild: node is not a child of this parent — skipping", child, this);
      return child;
    }
    return originalRemoveChild.call(this, child);
  } as typeof Node.prototype.removeChild;

  const originalInsertBefore = Node.prototype.insertBefore;
  Node.prototype.insertBefore = function (this: Node, newNode: Node, referenceNode: Node | null) {
    if (referenceNode && referenceNode.parentNode !== this) {
      if (import.meta.env.DEV) console.warn("insertBefore: reference node is not a child of this parent — appending instead", referenceNode, this);
      return originalInsertBefore.call(this, newNode, null);
    }
    return originalInsertBefore.call(this, newNode, referenceNode);
  } as typeof Node.prototype.insertBefore;
}

createRoot(document.getElementById("root")!).render(
  <AppErrorBoundary context="root">
    <App />
  </AppErrorBoundary>
);

// Lazy-load Sentry after first paint. The error boundary will also initialize
// Sentry on demand if a crash happens before this idle task runs.
if (import.meta.env.PROD) {
  const loadSentry = () => {
    import("./lib/sentry")
      .then(({ initSentry, Sentry }) => {
        initSentry();
        (window as unknown as { Sentry: typeof Sentry }).Sentry = Sentry;
      })
      .catch(() => {
        // Swallow - if Sentry fails to load, the app keeps working without telemetry.
      });
  };

  if (typeof window !== "undefined") {
    const w = window as Window & { requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void };
    if (typeof w.requestIdleCallback === "function") {
      w.requestIdleCallback(loadSentry, { timeout: 4000 });
    } else {
      setTimeout(loadSentry, 3000);
    }
  }
}
